import type { AIModel } from "@/services/api/models";
import {
  searchFlowApi,
  type SearchFlowResult,
} from "@/services/api/searchFlow";
import { useCallback, useEffect, useRef, useState } from "react";
// rolled back models speedup
import type { BrandAiTrendPureDict } from "@/services/api/brandAiMetricsTrend";
import type { BrandMetricsRates } from "@/services/api/brandMetricsRates";
import type { BrandMetricsTrendPoint } from "@/services/api/brandMetricsTrend";
import type { BrandRatesItem } from "@/services/api/BrandRates";
import type { ReputationKeywordsResponse } from "@/services/api/reputation";
import type { BrandComparisonData } from "@/services/mock";
import type { BrandSearchItem } from "@/types/api";

export interface UseSearchDetailFlowResult {
  // 数据状态
  models: AIModel[];
  brandMetrics: BrandMetricsRates | null;
  brandMetricsTrend?: BrandMetricsTrendPoint[];
  brandAiMetricsTrend?: BrandAiTrendPureDict;
  brandComparison?: BrandComparisonData; // 新增：品牌对比数据
  brandRatesList?: BrandRatesItem[];
  searchResults: BrandSearchItem[]; // 新增：搜索结果数据
  reputationKeywords?: ReputationKeywordsResponse; // 新增：品牌声誉关键词数据
  mediaCite?: any[];
  contentCite?: any[];
  isLoading: boolean;
  error: string | null;

  // 方法
  executeSearchFlow: (forceExecute?: boolean) => Promise<void>;
  refreshData: () => Promise<void>;
  updateData: (data: {
    brandMetrics?: BrandMetricsRates | null;
    brandMetricsTrend?: BrandMetricsTrendPoint[];
    brandAiMetricsTrend?: BrandAiTrendPureDict;
    brandComparison?: BrandComparisonData;
    brandRatesList?: BrandRatesItem[];
    reputationKeywords?: ReputationKeywordsResponse;
  }) => void;
}

export interface UseSearchDetailFlowParams {
  brandId?: string;
  categoryId?: string;
  keyword?: string;
  specificItem?: {
    // 新增：特定品牌项（包含名称信息）
    brandId: string;
    industryId: string;
    brandName?: string;
    industryName?: string;
  };
  autoExecute?: boolean; // 是否自动执行，默认true
  currentDays?: number; // 当前选择的时间范围（天数）
  currentModelId?: string; // 当前选择的AI平台ID
  startDate?: string; // 自定义开始日期 (YYYY-MM-DD)
  endDate?: string; // 自定义结束日期 (YYYY-MM-DD)
  qId?: string; // query ID
}

export function useSearchDetailFlow({
  brandId,
  categoryId,
  keyword,
  specificItem,
  autoExecute = true,
  currentDays = 7,
  currentModelId = "all",
  startDate,
  endDate,
  qId,
}: UseSearchDetailFlowParams): UseSearchDetailFlowResult {
  const [models, setModels] = useState<AIModel[]>([]);
  const [brandMetrics, setBrandMetrics] = useState<BrandMetricsRates | null>(
    null
  );
  const [brandMetricsTrend, setBrandMetricsTrend] = useState<
    BrandMetricsTrendPoint[] | undefined
  >(undefined);
  const [brandAiMetricsTrend, setBrandAiMetricsTrend] = useState<
    BrandAiTrendPureDict | undefined
  >(undefined);
  const [brandComparison, setBrandComparison] = useState<
    BrandComparisonData | undefined
  >(undefined); // 新增：品牌对比数据
  const [brandRatesList, setBrandRatesList] = useState<
    BrandRatesItem[] | undefined
  >(undefined);
  const [searchResults, setSearchResults] = useState<BrandSearchItem[]>([]); // 新增：搜索结果状态
  const [reputationKeywords, setReputationKeywords] = useState<
    ReputationKeywordsResponse | undefined
  >(undefined); // 新增：品牌声誉关键词数据
  const [mediaCite, setMediaCite] = useState<any[] | undefined>(undefined);
  const [contentCite, setContentCite] = useState<any[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 避免在 StrictMode 或快速重复渲染下重复执行
  const inFlightRef = useRef<boolean>(false);
  const lastFullRunKeyRef = useRef<string>(""); // 全量刷新的 key
  const lastLightRunKeyRef = useRef<string>(""); // 轻量刷新的 key
  const hasInitializedRef = useRef<boolean>(false);
  const prevInitKeyRef = useRef<string>("");

  // 优化 key 计算，分离全量刷新和轻量刷新
  const effectiveBrandId = brandId || specificItem?.brandId || "";
  const effectiveCategoryId = categoryId || specificItem?.industryId || "";
  // 全量刷新 key（不包含 days 和 modelId）
  const fullRunKey = `${effectiveBrandId}|${effectiveCategoryId}|${keyword || ""}`;
  // 轻量刷新 key（包含 days、modelId、自定义日期和 qId）
  const lightRunKey = `${effectiveBrandId}|${effectiveCategoryId}|${currentDays}|${currentModelId}|${startDate || ""}|${endDate || ""}|${qId || ""}`;

  // 执行搜索流程
  const executeSearchFlow = useCallback(
    async (forceExecute = false) => {
      // 始终检查并发请求，即使 forceExecute=true
      if (inFlightRef.current) {
        console.log("⏳ 搜索流程已在执行中，跳过本次调用");
        return;
      }

      // 使用全量刷新 key 进行去重检查
      if (!forceExecute && lastFullRunKeyRef.current === fullRunKey) {
        console.log("🔁 参数未变化，已执行过本轮，跳过重复请求");
        return;
      }

      console.log("🚀 开始执行搜索详情页流程:", {
        brandId,
        categoryId,
        keyword,
        specificItem,
      });
      console.log("🔑 fullRunKey:", fullRunKey);
      setIsLoading(true);
      setError(null);
      inFlightRef.current = true;

      try {
        let result: SearchFlowResult;

        if (specificItem?.brandId && specificItem?.industryId) {
          // 模式1: 如果有specificItem，使用指定品牌模式
          console.log("🎯 使用指定品牌模式");
          console.log("📞 获取指定品牌和行业数据");
          result = await searchFlowApi.executeSearchFlow(keyword || "", {
            brandId: specificItem.brandId,
            industryId: specificItem.industryId,
            brandName: specificItem.brandName,
            categoryName: specificItem.industryName,
            days: currentDays, // 使用当前选择的时间范围
            modelId: currentModelId, // 使用当前选择的AI平台
            startDate, // 自定义开始日期
            endDate, // 自定义结束日期
            qId, // query ID
          });
        } else if (brandId && categoryId) {
          // 模式1.5: 如果有brandId和categoryId但没有specificItem，使用指定品牌模式（向后兼容）
          console.log("🎯 使用指定品牌模式（兼容模式）");
          result = await searchFlowApi.executeSearchFlow(keyword || "", {
            brandId,
            industryId: categoryId,
            days: currentDays, // 使用当前选择的时间范围
            modelId: currentModelId, // 使用当前选择的AI平台
            startDate, // 自定义开始日期
            endDate, // 自定义结束日期
            qId, // query ID
          });

          // 获取该品牌的所有行业选项
          if (keyword) {
            try {
              const allBrandResults =
                await searchFlowApi.executeSearchFlow(keyword);
              if (
                allBrandResults.searchResults &&
                allBrandResults.searchResults.length > 0
              ) {
                // 过滤出相同品牌的所有行业选项
                const sameBrandResults = allBrandResults.searchResults.filter(
                  (item) => item.brandId === brandId
                );
                if (sameBrandResults.length > 0) {
                  result.searchResults = sameBrandResults;
                  console.log(
                    "🏷️ 获取到该品牌的所有行业选项:",
                    sameBrandResults.map((r) => r.categoryName)
                  );
                }
              }
            } catch (err) {
              console.warn("⚠️ 获取品牌所有行业选项失败，使用原始结果:", err);
            }
          }
        } else if (keyword) {
          // 模式2: 如果只有关键词，执行关键词搜索模式
          console.log("🔍 使用关键词搜索模式");
          result = await searchFlowApi.executeSearchFlow(keyword);
        } else {
          // 模式3: 没有足够参数，返回空结果
          console.log("⚠️ 缺少必要参数，返回空结果");
          result = {
            searchResults: [],
            models: [],
            brandMetrics: null,
          };
        }

        console.log("📊 搜索详情页流程结果:", result);

        // 更新状态
        // 先尝试使用缓存的 models；若接口返回 models 也写入缓存
        setModels(result.models);
        setBrandMetrics(result.brandMetrics);
        setSearchResults(result.searchResults || []);
        setBrandMetricsTrend(result.brandMetricsTrend);
        setBrandAiMetricsTrend(result.brandAiMetricsTrend);
        setBrandComparison(result.brandComparison); // 新增：设置品牌对比数据
        setBrandRatesList(result.brandRatesList);
        setReputationKeywords(result.reputationKeywords); // 新增：设置品牌声誉关键词数据
        setMediaCite(result.mediaCite);
        setContentCite(result.contentCite);

        console.log("✅ 搜索详情页流程完成");
        lastFullRunKeyRef.current = fullRunKey;
        hasInitializedRef.current = true;
      } catch (err: any) {
        console.error("❌ 搜索详情页流程失败:", err);
        setError(err?.message || "数据加载失败");
      } finally {
        setIsLoading(false);
        inFlightRef.current = false;
      }
    },
    [
      brandId,
      categoryId,
      keyword,
      specificItem?.brandId,
      specificItem?.industryId,
      currentDays,
      currentModelId,
    ]
  );

  // 刷新数据
  const refreshData = useCallback(async () => {
    await executeSearchFlow();
  }, [executeSearchFlow]);

  // 更新数据
  const updateData = useCallback(
    (data: {
      brandMetrics?: BrandMetricsRates | null;
      brandMetricsTrend?: BrandMetricsTrendPoint[];
      brandAiMetricsTrend?: BrandAiTrendPureDict;
      brandComparison?: BrandComparisonData;
      brandRatesList?: BrandRatesItem[];
      reputationKeywords?: ReputationKeywordsResponse;
      mediaCite?: any[];
      contentCite?: any[];
    }) => {
      if (data.brandMetrics !== undefined) {
        setBrandMetrics(data.brandMetrics);
      }
      if (data.brandMetricsTrend !== undefined) {
        setBrandMetricsTrend(data.brandMetricsTrend);
      }
      if (data.brandAiMetricsTrend !== undefined) {
        setBrandAiMetricsTrend(data.brandAiMetricsTrend);
      }
      if (data.brandComparison !== undefined) {
        setBrandComparison(data.brandComparison);
      }
      if (data.brandRatesList !== undefined) {
        setBrandRatesList(data.brandRatesList);
      }
      if (data.reputationKeywords !== undefined) {
        setReputationKeywords(data.reputationKeywords);
      }
      if (data.mediaCite !== undefined) {
        setMediaCite(data.mediaCite);
      }
      if (data.contentCite !== undefined) {
        setContentCite(data.contentCite);
      }
    },
    []
  );

  // 自动执行（初始化/关键词或品牌切换时）
  // 仅当 brandId/categoryId/keyword 组合变化时执行 executeSearchFlow
  useEffect(() => {
    if (!autoExecute) return;
    if (!keyword && (!brandId || !categoryId)) return;

    const initKey = `${effectiveBrandId}|${effectiveCategoryId}|${keyword || ""}`;
    if (prevInitKeyRef.current === initKey) return;
    prevInitKeyRef.current = initKey;
    // 强制执行一次，避免 lastRunKey 阻止
    executeSearchFlow(true);
  }, [
    autoExecute,
    effectiveBrandId,
    effectiveCategoryId,
    keyword,
    executeSearchFlow,
  ]);

  // rolled back pre-warm models

  // 时间范围/模型切换时只做轻量刷新
  useEffect(() => {
    if (!autoExecute) return;
    if (!hasInitializedRef.current) return;
    if (!effectiveBrandId || !effectiveCategoryId) return;

    // 使用独立的轻量刷新 key 进行去重检查
    if (lastLightRunKeyRef.current === lightRunKey) {
      console.log("🔁 轻量刷新参数未变化，跳过");
      return;
    }

    console.log("🔄 轻量刷新 effect 触发:", {
      effectiveBrandId,
      effectiveCategoryId,
      currentDays,
      currentModelId,
      lightRunKey,
    });

    const run = async () => {
      try {
        console.log("📡 调用 executeTimeRangeChange API...");
        // 如果有自定义日期，将 days 设为空字符串
        const effectiveDays = startDate && endDate ? "" : currentDays;
        const res = await searchFlowApi.executeTimeRangeChange({
          brandId: effectiveBrandId,
          categoryId: effectiveCategoryId,
          days: effectiveDays as any,
          modelId: currentModelId,
          startDate,
          endDate,
          qId,
        });
        // 写回状态
        setBrandMetrics(res.brandMetrics);
        setBrandMetricsTrend(res.brandMetricsTrend);
        setBrandAiMetricsTrend(res.brandAiMetricsTrend);
        setBrandRatesList(res.brandRatesList);
        setReputationKeywords(res.reputationKeywords);
        setMediaCite(res.mediaCite);
        setContentCite(res.contentCite);
        // 关键修复：更新品牌对比数据
        if (res.brandComparison) {
          console.log("✅ 更新 brandComparison 数据:", res.brandComparison);
          setBrandComparison(res.brandComparison);
        } else {
          console.log("⚠️ API 返回的 brandComparison 为空");
        }
        // 更新轻量刷新 key
        lastLightRunKeyRef.current = lightRunKey;
        console.log("✅ 轻量刷新完成");
      } catch (err: any) {
        console.error("❌ 时间范围/模型切换刷新失败:", err);
        setError(err?.message || "刷新失败");
      }
    };
    run();
    // 依赖仅关心轻量刷新触发条件
  }, [
    autoExecute,
    effectiveBrandId,
    effectiveCategoryId,
    currentDays,
    currentModelId,
    lightRunKey,
  ]);

  return {
    models,
    brandMetrics,
    brandMetricsTrend,
    brandAiMetricsTrend,
    brandComparison, // 新增：返回品牌对比数据
    brandRatesList,
    searchResults, // 新增：返回搜索结果
    reputationKeywords, // 新增：返回品牌声誉关键词数据
    mediaCite,
    contentCite,
    isLoading,
    error,
    executeSearchFlow,
    refreshData,
    updateData,
  };
}

export default useSearchDetailFlow;
